import React, { FC, useState } from 'react';
import { bindActionCreators } from 'redux';
import { RootState } from '../../redux/store';
import { useSelector, connect } from 'react-redux';
import * as protocolInteractors from '../../redux/interactors/ProtocolInteractors';
import * as userInteractors from '../../redux/interactors/UserInteractors';
import userService from '../../services/UserServices';
import CloseIcon from '@mui/icons-material/Close';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import { Checkbox, Box, Stack, Chip, Button } from '@mui/material';
import { Accordion, AccordionSummary, AccordionDetails, Typography } from '@mui/material';
import './Protocol.css';
import RoutineToggle from './RoutineToggle';
import RoutineTimerView from './RoutineTimerView';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import AccessTimeFilledIcon from '@mui/icons-material/AccessTimeFilled';
import AccessibilityIcon from '@mui/icons-material/Accessibility';
import ConsoltoButton from './ConsoltoButton';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useDispatch } from 'react-redux';
import { bodyActions } from '../../redux/slices/bodySlice';
import { lockCombination } from '../../appVersion';
import LockTest from './LockTest';

const bodyInterface: { [index: string]: any } = {
  head: 'Cuello',
  trapezius: 'Cuello',
  chest: '',
  stomach: '',
  leftShoulder: 'Hombro',
  leftArm: 'Brazo / Codo',
  leftHand: 'Muñeca / Mano',
  leftLeg: 'Pierna / Rodilla',
  leftFoot: 'Pie / Planta del Pie',
  rightShoulder: 'Hombro',
  rightArm: 'Brazo / Codo',
  rightHand: 'Muñeca / Mano',
  rightLeg: 'Pierna / Rodilla',
  rightFoot: 'Pie / Planta del Pie',
  lowerBack: 'Espalda Baja / Glúteo',
};

function getKeyByValue(value: any): string {
  for (const key in bodyInterface) {
    if (bodyInterface[key] === value) {
      return key;
    }
  }
  return ''; // Retorna undefined si no se encuentra ninguna coincidencia
}

interface DispatchProps {
  unselectProtocolInteractor: typeof protocolInteractors.unselectProtocolInteractor;
  saveProtocolInteractor: typeof userInteractors.saveProtocolInteractor;
  unsaveProtocolInteractor: typeof userInteractors.unsaveProtocolInteractor;
}

const Routine: FC<DispatchProps> = (props: DispatchProps) => {
  const protocol = useSelector((state: RootState) => state.protocol.protocol);
  const user = useSelector((state: RootState) => state.user);
  const protocolId = protocol ? protocol.id : NaN;
  const saved = user.saveds?.filter((saved) => saved.protocol_id === protocolId)[0];
  const [timer, setTimer] = useState(true);
  const dispatch = useDispatch();
  const filter = useSelector((state: RootState) => state.filter);
  const tags = filter.filters.filter((f) => f.protocolIds.includes(protocolId));

  const toggleHandler = (e: boolean) => {
    setTimer(e);
  };

  const saveProtocol = () => {
    userService.saveProtocol(protocol?.id, user?.id).then((response: any) => {
      if (+response.status === 201) {
        const new_saved = response?.data;
        props.saveProtocolInteractor({ value: new_saved });
      }
    });
  };

  const unsaveProtocol = () => {
    userService.unsaveProtocol(saved?.id).then((response: any) => {
      if (+response.status === 200) {
        props.unsaveProtocolInteractor({ value: saved?.id });
      }
    });
  };

  const showOptions = () => {
    dispatch(bodyActions.select(getKeyByValue(protocol?.bodyPart)));
  };

  return (
    <Card sx={{ width: '100vw', height: '90vh' }}>
      <CardHeader
        title={<h3 className="App-logo-text">{protocol?.title}</h3>}
        action={
          <Checkbox
            checked={saved ? true : false}
            onClick={saved ? unsaveProtocol : saveProtocol}
            icon={<BookmarkBorderIcon />}
            checkedIcon={<BookmarkIcon />}
            sx={{ '& .MuiSvgIcon-root': { fontSize: 28 } }}
          />
        }
      />
      {lockCombination.includes(user.business_id ?? 0) && <LockTest />}
      <RoutineToggle onChange={toggleHandler} />
      <div className="scroll-box">
        {timer ? (
          <RoutineTimerView />
        ) : (
          <Box sx={{ display: 'inline', witdh: '100vw', height: '60vh' }}>
            <iframe
              width="100%"
              height="100%"
              src={protocol?.videoUrl}
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; modestbranding; gyroscope; picture-in-picture"
              allowFullScreen
              title="Embedded youtube"
            />
          </Box>
        )}
        <Box sx={{ display: { xs: 'block', sm: 'block', md: 'inline' }, width: '100vw', overflow: 'auto' }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ zIndex: 1000 }}>
            <Stack direction="column" sx={{ minWidth: '50%', width: '100vw', maxWidth: '100vw', overflow: 'hidden' }}>
              <Stack
                justifyContent="flex-start"
                alignItems="flex-start"
                direction="row"
                spacing={1}
                sx={{ padding: '10px' }}
                display="inline-block">
                <Chip color="info" sx={{ marginY: '5px' }} label={protocol?.time} icon={<AccessTimeFilledIcon />} />
                <Chip color="info" sx={{ marginY: '5px' }} label={protocol?.bodyPart} icon={<AccessibilityIcon />} />
                {tags.map((tag) => (
                  <Chip color="info" sx={{ marginY: '5px' }} key={`t-${tag.id}`} label={tag.name} />
                ))}
              </Stack>
              <Stack direction="column" sx={{ margin: '10px' }}>
                <ConsoltoButton />
                <Button sx={{ fontWeight: 'bold', marginY: '5px' }} onClick={showOptions}>
                  VER ALTERNATIVAS
                </Button>
              </Stack>
            </Stack>
            <Stack
              direction="column"
              sx={{ minWidth: '50%', width: '100vw', overflow: 'hidden', paddingBottom: '30vh' }}>
              {protocol?.steps?.map((step, index) => (
                <Accordion key={`step${index}`}>
                  <AccordionSummary
                    expandIcon={<CloseIcon />}
                    aria-controls={`panel${index}a-content`}
                    id={`panel${index}a-header`}
                    sx={{ paddingY: 0, marginY: 0 }}>
                    <h1 className="App-logo-text">{step.subtitle}</h1>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography variant="h6" sx={{ margin: '0px', padding: '0px' }}>
                      <div className="md">
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>{step.content}</ReactMarkdown>
                      </div>
                    </Typography>
                    <img src={step.imgUrl} width={'100%'} />
                  </AccordionDetails>
                </Accordion>
              ))}
            </Stack>
          </Stack>
        </Box>
      </div>
    </Card>
  );
};

const mapDispatchToProps = (dispatch: any): DispatchProps => ({
  ...bindActionCreators(
    {
      ...protocolInteractors,
      ...userInteractors,
    },
    dispatch,
  ),
});

export default connect(null, mapDispatchToProps)(Routine);
